import React, { memo, useMemo, RefObject, useImperativeHandle, MouseEventHandler } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import rrulePlugin from '@fullcalendar/rrule';
import interactionPlugin, { Draggable, DateClickArg } from '@fullcalendar/interaction';
import BootstrapTheme from '@fullcalendar/bootstrap';
import { CalendarOptions, EventInput, CalendarApi, EventClickArg } from '@fullcalendar/core';
import { toMoment } from '@core/utils/time';
import { Moment } from 'moment';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import momentPlugin from '@fullcalendar/moment';
import timeGridPlugin from '@fullcalendar/timegrid';
import { WithTooltip } from '../WithTooltip';

export enum CalendarView {
  month = 'dayGridMonth',
  week = 'timeGridWeek',
  day = 'dayGridDay',
  resource = 'resourceTimeGridDay',
}

export interface CalendarEvent<T = any> extends Omit<EventInput, 'start' | 'end'> {
  start: number;
  end?: number;
  item: T;
  strikeThrough?: boolean;
}

export interface CalendarHandle {
  calendar: () => CalendarApi | null;
}

export interface CalendarProps<T> extends Omit<CalendarOptions, 'events'> {
  events: CalendarEvent<T>[];
  onDayClick?: (date: Moment, event: DateClickArg) => void;
  onEventClick?: (id: string, item: T, event: EventClickArg) => void;
  onEventChange?: (id: string, item: T, start: number, end?: number, newResourceId?: string) => void;
  types?: CalendarView[];
  handlesRef?: RefObject<CalendarHandle>;
  enableDragAndDrop?: boolean; // Enable drag/drop and resource editing features
}

export const Calendar = memo(<T extends any>(props: CalendarProps<T>) => {
  const { onDayClick, onEventClick, onEventChange, events, types, handlesRef, enableDragAndDrop = false, ...rest } = props;
  const ref = React.useRef<FullCalendar | null>();

  useImperativeHandle(props.handlesRef, () => ({
    calendar: () => (ref.current ? ref.current!.getApi() : null),
  }));

  const calEvents = useMemo(() => {
    return events.map(e => ({
      ...e,
      start: toMoment(e.start).toDate(),
      end: e.end ? toMoment(e.end).toDate() : undefined,
      extendedProps: e.item,
      isStrikeThrough: e.strikeThrough || false,
    }));
  }, [events]);

  return (
    <FullCalendar
      scrollTimeReset={false}
      schedulerLicenseKey="0031686291-fcs-1698073537"
      ref={ref as any}
      plugins={[momentPlugin, BootstrapTheme, dayGridPlugin, rrulePlugin, interactionPlugin, resourceTimeGridPlugin, timeGridPlugin]}
      slotDuration={'00:15:00'}
      handleWindowResize={true}
      themeSystem="bootstrap"
      headerToolbar={{
        left: 'prev,next today',
        center: 'title',
        right: types ? (types.length > 1 ? types.join(',') : undefined) : 'dayGridMonth,dayGridWeek,dayGridDay',
      }}
      eventDisplay="block"
      events={calEvents}
      eventBorderColor="transparent"
      editable={enableDragAndDrop}
      allDaySlot={false}
      eventStartEditable={enableDragAndDrop}
      eventDurationEditable={enableDragAndDrop}
      eventResourceEditable={enableDragAndDrop}
      droppable={enableDragAndDrop}
      selectable={true}
      dateClick={event => onDayClick && onDayClick(toMoment(event.date), event)}
      eventClick={e => onEventClick && onEventClick(e.event.id, e.event.extendedProps.item, e)}
      eventChange={e => {
        if (onEventChange) {
          // if (onEventChange && enableDragAndDrop) {
          const newResourceId = e.event.getResources()[0]?.id;
          onEventChange(
            e.event.id,
            e.event.extendedProps.item,
            toMoment(e.event.start).unix(),
            e.event.end ? toMoment(e.event.end).unix() : undefined,
            newResourceId,
          );
        }
      }}
      eventContent={eventInfo => {
        const isStrikeThrough = eventInfo.event.extendedProps.isStrikeThrough;
        const reminders: { value: number; unit: string }[] = eventInfo.event.extendedProps?.reminders || [];
        const hasReminders = reminders.length > 0;

        const unitAbbrev = (unit: string, value: number) => {
          const map: Record<string, string> = { minutes: 'min', hours: 'hr', days: 'day', weeks: 'week' };
          const short = map[unit] || unit;
          return value === 1 ? short : `${short}s`;
        };

        const reminderTooltip = hasReminders ? (
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Reminders</div>
            {reminders.map((r, i) => (
              <div key={i}>• {r.value} {unitAbbrev(r.unit, r.value)} before</div>
            ))}
          </div>
        ) : null;

        return (
          <div style={{ textDecoration: isStrikeThrough ? 'line-through' : 'none', overflow: 'hidden' }}>
            <div className="fc-event-time">
              {eventInfo.timeText}
              {hasReminders && (
                <>
                  {' '}
                  <WithTooltip tooltip={reminderTooltip}>
                    <span style={{ flexShrink: 0, fontSize: 13, opacity: 0.9, lineHeight: 1, cursor: 'default' }}>
                      <i className="fa fa-bell" />
                    </span>
                  </WithTooltip>
                </>
              )}
            </div>
            <div className="fc-event-title" style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{eventInfo.event.title}</span>
            </div>
          </div>
        );
      }}
      {...rest}
      //   key={props.initialView}
    />
  );
});
